import { dedupeAnchorId, extractHeadingAnchors, slugifyAnchor } from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/useRichTextEditorRenderer';
import { parseStringToTipTapJson } from '~kui/components/KUI/Core/Input/RichTextEditor/useRichTextEditor';

import type { Article } from '~/types/Learn/Article';

import { fromCampaignDateTime } from '~/utils/campaign';

export interface ArticleGuideItem {
  id: string;
  label: string;
}

export const extractArticleGuideItems = (body: string): ArticleGuideItem[] => {
  if (!body) {
    return [];
  }

  const doc = parseStringToTipTapJson(body);

  return extractHeadingAnchors(doc)
    .filter(item => item.level === 2 && item.id !== '' && item.label.trim() !== '')
    .map(({ id, label }) => ({ id, label }));
};

/**
 * Anchor ids the rendered body's headings already claim, so step anchors can be
 * deduped against them — a step titled the same as an intro H2 would otherwise
 * emit a duplicate id and every link to it would scroll to the heading instead.
 */
export const extractArticleBodyAnchorIds = (body: string): string[] => {
  if (!body) {
    return [];
  }

  return extractHeadingAnchors(parseStringToTipTapJson(body))
    .map(item => item.id)
    .filter(id => id !== '');
};

export interface ArticleSectionGuideItem extends ArticleGuideItem {
  key: string;
}

/**
 * Keyed equivalent of extractArticleGuideItems for authored section headings.
 * Each input keeps its key in the output (`id: ''` for blank/unslugifiable
 * headings), so callers look anchors up by key instead of pairing arrays by
 * index.
 */
export const extractArticleSectionGuideItems = (
  sections: { key: string; heading: string }[],
  reservedIds: string[] = [],
): ArticleSectionGuideItem[] => {
  const usedIds = new Set<string>(reservedIds);

  return sections.map(({ key, heading }) => {
    const label = heading.trim();
    const base = slugifyAnchor(label);

    if (!base) {
      return { key, id: '', label };
    }

    return { key, id: dedupeAnchorId(base, usedIds), label };
  });
};

/**
 * A closing section exists when either its heading or its rich-text body is
 * authored — the render gate and the anchor-list entry share this rule.
 */
export const hasArticleClosingSection = ({ heading, body }: { heading: string; body: string }): boolean =>
  hasTipTapContent(body) || hasText(heading);

export interface ArticleBylineDays {
  publishedDay: string | null;
  lastUpdatedDay: string | null;
}

/**
 * The byline's calendar days (`YYYY-MM-DD`) in the cluster timezone. The published day is the
 * editor's back-date, else the first publish. A last-updated day only exists for a publish after
 * the first one that also lands after the published day, so a back-date alone, or a same-day
 * republish, never reads as an update. The byline and the article's SEO meta both follow this.
 */
export const articleBylineDays = (
  article: Pick<Article, 'publishedAt' | 'firstPublishedAt' | 'lastPublishedAt' | 'displayPublishedAt'>,
  timezone: string,
): ArticleBylineDays => {
  const publishedDay = article.displayPublishedAt ?? fromCampaignDateTime(article.publishedAt, timezone);
  const firstDay = fromCampaignDateTime(article.firstPublishedAt, timezone);
  const lastDay = fromCampaignDateTime(article.lastPublishedAt, timezone);
  const isUpdate = !!lastDay && !!publishedDay && !!firstDay && lastDay > firstDay && lastDay > publishedDay;

  return { publishedDay, lastUpdatedDay: isUpdate ? lastDay : null };
};
